import { MarkerType } from '@xyflow/react';
import { processEdges } from './edgeUtils';

/**
 * Creates nodes for React Flow from the hierarchical data
 * @param {Array} nodeLayouts - Array of hierarchical node data
 * @param {Array} nodes - Array of all nodes
 * @param {Array} visualNodeLayoutFlow - Array of node layout positions
 * @param {Function} onChildMouseEnter - Mouse enter handler for child nodes
 * @param {Function} onChildClick - Click handler for child nodes
 * @param {Boolean} isSimplifiedView - Flag to indicate if the view is simplified, i.e. no child nodes are shown
 * @returns {Array} Array of node objects for React Flow
 */
export const createNodesFromHierarchy = (
  nodeLayouts,
  nodes,
  visualNodeLayoutFlow,
  onChildMouseEnter,
  onChildClick,
  isSimplifiedView
) => {
  return nodeLayouts.map(item => {
    // Find the main node data
    const nodeData = nodes.find(n => n.id === item.id);
    // Find layout position
    const layout = visualNodeLayoutFlow?.find(l => l.id === item.id) || {};

    // Get child node data
    const childNodes = item.children.map(child => {
      const childNodeData = nodes.find(n => n.id === child.id);
      return {
        ...childNodeData,
        isRelationship: nodeLayouts.find(n => n.id === child.id)
      };
    });

    return {
      id: item.id.toString(),
      type: 'customNode',
      selectable: false, // we don't want to be able to select nodes because it adds a weird styling
      position: { x: layout.x ?? 0, y: layout.y ?? 0 },
      data: {
        node: nodeData,
        childNodes: childNodes,
        isExpanded: true,
        isSimplifiedView: isSimplifiedView,
        onChildMouseEnter: onChildMouseEnter,
        onChildClick: onChildClick
      }
    };
  });
};

/**
 * Creates edges for React Flow from the graph data
 * Applies special logic for multiple edges between the same nodes
 * @param {Object} graph - Graph data containing edges
 * @param {Boolean} isSimplifiedView - Flag to indicate if the view is simplified, i.e. no child nodes are shown
 * @returns {Array} Array of edge objects for React Flow
 */
export const createEdgesFromHierarchy = (graph, isSimplifiedView) => {
  if (!graph.edges) return [];
  // Create basic edge objects from graph edges
  const reactFlowEdges = graph.edges.map(edge => {
    const label = edge.shouldShowEdgeName ? edge.link.name : '';
    // Create a unique ID for each edge
    const edgeId = `edge-${edge.from}-${edge.to}-${edge.link.uri}`;

    return {
      type: 'floating',
      id: edgeId,
      source: edge.from.toString(),
      target: edge.to.toString(),
      label: label,
      // We'll always show edge labels for clarity in the new graph
      data: {
        edge: edge, // Store the original edge data for reference
        isSimplifiedView: isSimplifiedView,
        showLabel: true // Always show labels in the new graph for clarity
      },
      style: {
        stroke: '#999',
        strokeWidth: 2
      },
      markerEnd: {
        type: MarkerType.Arrow,
        width: 20,
        height: 20
      }
    };
  });

  // Process edges to handle multiple connections between the same nodes
  // This applies curve properties to edges based on their relationships
  return processEdges(reactFlowEdges, {
    baseCurveStrength: 0.15, // Adjust to match original implementation
    isReactFlow: true
  });
};
